import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Text } from '@fluentui/react-components';
import { BasicButton } from '@components/common/BasicButton';
import { telemetry } from '@/services/telemetry';
import { useErrorBoundaryStyles } from './ErrorBoundary.styles';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

function FallbackUI() {
  const styles = useErrorBoundaryStyles();
  return (
    <div className={styles.root}>
      <Text as="h1" className={styles.title}>
        Something went wrong
      </Text>
      <Text className={styles.message}>An unexpected error occurred. Please try again.</Text>
      <BasicButton
        variant="primary"
        size="medium"
        onClick={() => {
          window.location.href = '/';
        }}
      >
        Go Home
      </BasicButton>
    </div>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    telemetry.trackException(error, 'ErrorBoundary', {
      componentStack: errorInfo.componentStack ?? '',
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <FallbackUI />;
    }
    return this.props.children;
  }
}

(ErrorBoundary as unknown as { displayName: string }).displayName = 'ErrorBoundary';
